import React, { useMemo } from "react";
import type { ReactNode } from "react";
import { useAuth } from 'tecrmi_components/useAuth';
import { HttpClient, setGlobalAccessTokenFn } from '@utils/httpClient';
import { useExtraction } from "@hooks/extraction/useExtraction";
import { LoadingScreen } from "@xplatform/design-system";
import { useDoctype } from "@src/hooks/flow/useDoctype";
import { ApplicationContext, type IApplicationContextType } from "./ApplicationContext";

interface IApplicationContextProviderProps {
    children: ReactNode;
}

export const ApplicationProvider: React.FC<IApplicationContextProviderProps> = ({ children }) => {
    const { getValidAccessToken, currentSession, loading } = useAuth();
    const [tokenReady, setTokenReady] = React.useState(false);

    // Set global token accessor so all HttpClient instances can use BeeHive token
    React.useEffect(() => {
        setGlobalAccessTokenFn(getValidAccessToken);
    }, [getValidAccessToken]);

    // Store token in localStorage for WebSocket connections
    // and signal that the token is ready for API calls
    React.useEffect(() => {
        const storeToken = async () => {
            const token = await getValidAccessToken();
            if (token) {
                localStorage.setItem("accessToken", token);
                setTokenReady(true);
            }
        };
        if (currentSession) {
            storeToken();
        } else {
            localStorage.removeItem("accessToken");
            setTokenReady(false);
        }
    }, [currentSession, getValidAccessToken]);

    const extractionState = useExtraction();
    const doctypeState = useDoctype();

    const httpClient = useMemo(() => {
        const baseUrl = import.meta.env.VITE_EXTRACTION_API_BASE_URL || '';
        // HttpClient expects string | undefined, but getValidAccessToken returns string | null
        const getToken = async () => (await getValidAccessToken()) ?? undefined;
        return new HttpClient(baseUrl, getToken);
    }, [getValidAccessToken]);

    if (loading || (currentSession && !tokenReady)) {
        return <LoadingScreen message="Checking authentication..." />;
    }

    if (!currentSession) {
        return <>{children}</>;
    }

    const contextValue: IApplicationContextType = {
        extraction: extractionState,
        doctype: doctypeState,
        httpClient,
    };

    return (
        <ApplicationContext.Provider value={contextValue}>
            {children}
        </ApplicationContext.Provider>
    );
};

